import React, { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { Box, Typography, Paper, Grid, Skeleton } from "@mui/material";
import queryString from "query-string";
import { useDispatch, useSelector } from "react-redux";
import { Target, Bot, BarChart3, GitCompare } from "lucide-react";
import { config } from "../../config";
import {
  adInsightsPath,
  adStrategyPath,
  competitiveAnalysisPath,
  homeRoutePath,
} from "../Router";
import ICPResearchView from "./ICPResearchView";
import AdStrategyView from "./AdStrategyView";
import AdInsightsView from "./AdInsightsView";
import CompetitiveAnalysisView from "./CompetitiveAnalysisView";
import CommonLayout from "../../components/CommonLayout";
import { Project } from "../../interfaces/Project";
import ProjectService from "../../services/Project";
import SessionService from "../../services/Session";
import { setUser } from "../../redux/actions/user";
import { logIssueNetworkRequestError } from "../../utils/Loggers";
import { displayToastMessage } from "../../utils/Toasts";

const SelectedProject = () => {
  const dispatch = useDispatch();
  const reduxUser: any = useSelector((state: any) => state.user);
  const { projectId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [project, setProject] = useState<Project | any>(
    config.inProd ? null : config.mocks.projects[0]
  );
  const [selectedAgent, setSelectedAgent] = useState<string | any>(null);

  const fetchProject = useCallback(
    async (userId: string) => {
      try {
        setIsLoading(true);
        const projectService = new ProjectService();
        const response = await projectService.getProject(
          projectId ? projectId : "",
          userId
        );
        setProject(response.project);
      } catch (error) {
        console.error("Search failed:", error);
        displayToastMessage(
          "error",
          "Failed to search projects. Please try again."
        );
      } finally {
        setIsLoading(false);
      }
    },
    [projectId]
  );

  const handleInitialLoad = useCallback(async () => {
    try {
      if (reduxUser.user && reduxUser.user.id) {
        await fetchProject(reduxUser.user.id);
        return;
      }
      const parsedQuery = queryString.parse(location.search);
      if (parsedQuery.email && parsedQuery.view === "activate") {
        navigate(homeRoutePath, {
          replace: true,
        });
        return;
      }

      const sessionService = new SessionService();
      const sessionData = await sessionService.reviewSession();
      if (!sessionData.success) {
        navigate(homeRoutePath, { replace: true });
        return;
      }

      dispatch(setUser(sessionData.userSessionData));

      await fetchProject(sessionData.userSessionData.id);
    } catch (error: any) {
      logIssueNetworkRequestError("Projects.handleInitialLoad", error);
      navigate(homeRoutePath, { replace: true });
    }
  }, [dispatch, location, navigate, fetchProject, reduxUser.user]);

  useEffect(() => {
    handleInitialLoad();
  }, [handleInitialLoad]);

  const agents: any = [
    {
      id: "icp",
      name: "ICP Research Agent",
      icon: Target,
      description:
        "Research and identify ideal customer profiles for targeted advertising",
      route: null,
    },
    {
      id: "strategy",
      name: "Ad Strategy Agent",
      icon: Bot,
      description: "Generate multi-platform ad strategies and recommendations",
      route: adStrategyPath.replace(":projectId", projectId || ""),
    },
    {
      id: "insights",
      name: "Ad Insights Agent",
      icon: BarChart3,
      description:
        "Analyze campaign performance and identify optimization opportunities",
      route: adInsightsPath.replace(":projectId", projectId || ""),
    },
    {
      id: "competitive",
      name: "Competitive Analysis Agent",
      icon: GitCompare,
      description: "Analyze competitor strategies and market trends",
      route: competitiveAnalysisPath.replace(":projectId", projectId || ""),
    },
  ];

  const renderAgentCards = () => {
    if (!selectedAgent) {
      return (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "repeat(2, 1fr)" },
            gap: 3,
          }}
        >
          {agents.map((agent: any) => (
            <Paper
              key={agent.id}
              elevation={0}
              sx={{
                p: 3,
                cursor: "pointer",
                border: "1px solid",
                borderColor: "rgba(0, 0, 0, 0.08)",
                borderRadius: "12px",
                backgroundColor: "white",
                transition: "all 0.2s ease",
                "&:hover": {
                  transform: "translateY(-4px)",
                  boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.1)",
                  borderColor: "rgba(239, 68, 68, 0.3)",
                },
              }}
              onClick={() => {
                if (agent.route) {
                  navigate(agent.route);
                } else {
                  setSelectedAgent(agent.id);
                }
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  height: "100%",
                }}
              >
                <Box
                  sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: 44,
                      height: 44,
                      borderRadius: "10px",
                      backgroundColor: "rgba(239, 68, 68, 0.08)",
                    }}
                  >
                    <agent.icon
                      style={{ color: "#EF4444", width: 20, height: 20 }}
                    />
                  </Box>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 600,
                      fontSize: "1.1rem",
                      color: "#111827",
                    }}
                  >
                    {agent.name}
                  </Typography>
                </Box>

                <Typography
                  variant="body2"
                  sx={{
                    mb: 2.5,
                    color: "#4B5563",
                    fontSize: "0.95rem",
                    lineHeight: 1.5,
                    flex: 1,
                  }}
                >
                  {agent.description}
                </Typography>

                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    mt: "auto",
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "0.875rem",
                      fontWeight: 600,
                      color: "#EF4444",
                      display: "flex",
                      alignItems: "center",
                      "& svg": {
                        ml: 0.5,
                        transition: "transform 0.2s",
                      },
                      "&:hover svg": {
                        transform: "translateX(3px)",
                      },
                    }}
                  >
                    View
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M5 12H19M19 12L12 5M19 12L12 19"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </Typography>
                </Box>
              </Box>
            </Paper>
          ))}
        </Box>
      );
    }

    switch (selectedAgent) {
      case "icp":
        return <ICPResearchView onBack={() => setSelectedAgent(null)} />;
      case "strategy":
        return <AdStrategyView onBack={() => setSelectedAgent(null)} />;
      case "insights":
        return <AdInsightsView onBack={() => setSelectedAgent(null)} />;
      case "competitive":
        return (
          <CompetitiveAnalysisView onBack={() => setSelectedAgent(null)} />
        );
      default:
        return null;
    }
  };

  const renderProjectDetails = () => (
    <Grid container spacing={3} sx={{ mt: 4 }}>
      {/* Company Overview */}
      <Grid item xs={12} md={6}>
        <Paper
          elevation={0}
          sx={{
            p: 3,
            border: "1px solid rgba(0, 0, 0, 0.08)",
            borderRadius: "12px",
            height: "100%",
          }}
        >
          <Typography
            variant="h6"
            sx={{
              mb: 2.5,
              fontWeight: 600,
              fontSize: "1.1rem",
              color: "#111827",
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <Box
              component="span"
              sx={{
                display: "inline-flex",
                backgroundColor: "rgba(239, 68, 68, 0.08)",
                p: 0.7,
                borderRadius: "6px",
              }}
            >
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M3 9L12 4L21 9V20H3V9Z"
                  stroke="#EF4444"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M9 20V14H15V20"
                  stroke="#EF4444"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </Box>
            Company Overview
          </Typography>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "1fr 2fr",
              gap: 2,
            }}
          >
            {[
              ["Industry", project.basicCompanyInfo.industrySector],
              [
                "Company Size",
                `${project.basicCompanyInfo.companySize} employees`,
              ],
              ["Revenue", project.basicCompanyInfo.annualRevenueRange],
              [
                "Locations",
                project.basicCompanyInfo.geographicLocations.join(", "),
              ],
              ["Stage", project.basicCompanyInfo.currentGrowthStage],
              [
                "Years Active",
                `${project.basicCompanyInfo.yearsInBusiness} years`,
              ],
            ].map(([key, value], index) => (
              <React.Fragment key={key}>
                <Typography
                  sx={{
                    color: "#6B7280",
                    fontSize: "0.9rem",
                    fontWeight: 500,
                  }}
                >
                  {key}
                </Typography>
                <Typography
                  sx={{
                    color: "#111827",
                    fontSize: "0.95rem",
                    fontWeight: index === 0 ? 600 : 400,
                  }}
                >
                  {value}
                </Typography>
              </React.Fragment>
            ))}
          </Box>
        </Paper>
      </Grid>

      {/* Product Details */}
      <Grid item xs={12} md={6}>
        <Paper
          elevation={0}
          sx={{
            p: 3,
            border: "1px solid rgba(0, 0, 0, 0.08)",
            borderRadius: "12px",
            height: "100%",
          }}
        >
          <Typography
            variant="h6"
            sx={{
              mb: 2.5,
              fontWeight: 600,
              fontSize: "1.1rem",
              color: "#111827",
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <Box
              component="span"
              sx={{
                display: "inline-flex",
                backgroundColor: "rgba(239, 68, 68, 0.08)",
                p: 0.7,
                borderRadius: "6px",
              }}
            >
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M21 16V7.2C21 6.0799 21 5.51984 20.782 5.09202C20.5903 4.71569 20.2843 4.40973 19.908 4.21799C19.4802 4 18.9201 4 17.8 4H6.2C5.07989 4 4.51984 4 4.09202 4.21799C3.71569 4.40973 3.40973 4.71569 3.21799 5.09202C3 5.51984 3 6.0799 3 7.2V16.8C3 17.9201 3 18.4802 3.21799 18.908C3.40973 19.2843 3.71569 19.5903 4.09202 19.782C4.51984 20 5.0799 20 6.2 20H17.8C18.9201 20 19.4802 20 19.908 19.782C20.2843 19.5903 20.5903 19.2843 20.782 18.908C21 18.4802 21 17.9201 21 16.8V16Z"
                  stroke="#EF4444"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M9 4V2M15 4V2M3.5 9H20.5"
                  stroke="#EF4444"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </Box>
            Product Information
          </Typography>

          <Typography
            variant="subtitle2"
            sx={{
              color: "#6B7280",
              fontWeight: 600,
              fontSize: "0.85rem",
              mb: 0.7,
            }}
          >
            Core Description
          </Typography>
          <Typography
            sx={{
              mb: 2.5,
              color: "#374151",
              fontSize: "0.95rem",
              lineHeight: 1.6,
            }}
          >
            {project.productServiceDetails.coreDescription}
          </Typography>

          <Typography
            variant="subtitle2"
            sx={{
              color: "#6B7280",
              fontWeight: 600,
              fontSize: "0.85rem",
              mb: 0.7,
            }}
          >
            Key Features
          </Typography>
          <Box component="ul" sx={{ pl: 2, mt: 0, mb: 0 }}>
            {project.productServiceDetails.keyFeaturesBenefits.map(
              (feature: any, idx: number) => (
                <Typography
                  component="li"
                  key={idx}
                  sx={{
                    color: "#374151",
                    fontSize: "0.95rem",
                    mb: 1,
                    "&:last-child": {
                      mb: 0,
                    },
                  }}
                >
                  {feature}
                </Typography>
              )
            )}
          </Box>
        </Paper>
      </Grid>

      {/* Market Position */}
      <Grid item xs={12} md={6}>
        <Paper
          elevation={0}
          sx={{
            p: 3,
            border: "1px solid rgba(0, 0, 0, 0.08)",
            borderRadius: "12px",
            height: "100%",
          }}
        >
          <Typography
            variant="h6"
            sx={{
              mb: 2.5,
              fontWeight: 600,
              fontSize: "1.1rem",
              color: "#111827",
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <Box
              component="span"
              sx={{
                display: "inline-flex",
                backgroundColor: "rgba(239, 68, 68, 0.08)",
                p: 0.7,
                borderRadius: "6px",
              }}
            >
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z"
                  stroke="#EF4444"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M12 16C14.2091 16 16 14.2091 16 12C16 9.79086 14.2091 8 12 8C9.79086 8 8 9.79086 8 12C8 14.2091 9.79086 16 12 16Z"
                  stroke="#EF4444"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M12 2V4M12 20V22M2 12H4M20 12H22M17.6569 6.34315L19.0711 4.92893M19.0711 19.0711L17.6569 17.6569M6.34315 6.34315L4.92893 4.92893M4.92893 19.0711L6.34315 17.6569"
                  stroke="#EF4444"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </Box>
            Market Position
          </Typography>

          <Typography
            variant="subtitle2"
            sx={{
              color: "#6B7280",
              fontWeight: 600,
              fontSize: "0.85rem",
              mb: 0.7,
            }}
          >
            Target Market
          </Typography>
          <Typography
            sx={{
              mb: 2.5,
              color: "#374151",
              fontSize: "0.95rem",
              lineHeight: 1.6,
            }}
          >
            {project.currentMarketPosition.targetMarketDescription}
          </Typography>

          <Typography
            variant="subtitle2"
            sx={{
              color: "#6B7280",
              fontWeight: 600,
              fontSize: "0.85rem",
              mb: 0.7,
            }}
          >
            Main Competitors
          </Typography>
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            {project.currentMarketPosition.mainCompetitors.map(
              (competitor: any, idx: number) => (
                <Typography
                  key={idx}
                  sx={{
                    bgcolor: "rgba(239, 68, 68, 0.08)",
                    color: "#EF4444",
                    px: 1.5,
                    py: 0.5,
                    borderRadius: "6px",
                    fontSize: "0.85rem",
                    fontWeight: 500,
                  }}
                >
                  {competitor}
                </Typography>
              )
            )}
          </Box>
        </Paper>
      </Grid>

      {/* Business Goals */}
      <Grid item xs={12} md={6}>
        <Paper
          elevation={0}
          sx={{
            p: 3,
            border: "1px solid rgba(0, 0, 0, 0.08)",
            borderRadius: "12px",
            height: "100%",
          }}
        >
          <Typography
            variant="h6"
            sx={{
              mb: 2.5,
              fontWeight: 600,
              fontSize: "1.1rem",
              color: "#111827",
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <Box
              component="span"
              sx={{
                display: "inline-flex",
                backgroundColor: "rgba(239, 68, 68, 0.08)",
                p: 0.7,
                borderRadius: "6px",
              }}
            >
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M4 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V4C20 3.46957 19.7893 2.96086 19.4142 2.58579C19.0391 2.21071 18.5304 2 18 2H4C3.46957 2 2.96086 2.21071 2.58579 2.58579C2.21071 2.96086 2 3.46957 2 4V20C2 20.5304 2.21071 21.0391 2.58579 21.4142C2.96086 21.7893 3.46957 22 4 22Z"
                  stroke="#EF4444"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M16 11.37C15.9395 11.3099 15.8697 11.2601 15.793 11.222C15.7163 11.1839 15.634 11.1582 15.55 11.146C15.4663 11.1214 15.3797 11.1091 15.293 11.11C15.2063 11.1109 15.1197 11.1249 15.036 11.152C14.9582 11.1791 14.8852 11.2173 14.82 11.265C14.7533 11.3158 14.694 11.3755 14.644 11.442C14.592 11.5086 14.5495 11.5827 14.518 11.662C14.4844 11.7432 14.4624 11.8288 14.453 11.916C14.4444 12.0035 14.4484 12.0918 14.465 12.178C14.4846 12.2643 14.5142 12.3485 14.553 12.429C14.5925 12.5072 14.6414 12.5806 14.698 12.648C14.7597 12.7145 14.8297 12.7745 14.907 12.827C14.9866 12.8763 15.0732 12.915 15.164 12.942C15.2552 12.9694 15.3502 12.9856 15.446 12.99C15.5417 12.9961 15.6379 12.9908 15.732 12.974C15.8233 12.9569 15.9122 12.9291 15.997 12.891C16.0791 12.8532 16.1563 12.8057 16.227 12.75C16.2952 12.6932 16.3568 12.6295 16.411 12.56C16.4633 12.4904 16.5078 12.4152 16.544 12.336C16.5791 12.2562 16.6057 12.1729 16.623 12.087C16.6405 12.0005 16.6487 11.9121 16.647 11.824C16.6457 11.7357 16.6345 11.6476 16.614 11.562C16.5939 11.4769 16.5649 11.3944 16.528 11.317C16.4909 11.2383 16.4452 11.1644 16.392 11.097C16.3373 11.0284 16.2746 10.9661 16.206 10.911C16.1361 10.8563 16.0593 10.811 15.977 10.776C15.8942 10.7404 15.8066 10.7152 15.717 10.701C15.63 10.6865 15.5412 10.6807 15.453 10.684C15.3649 10.6862 15.2768 10.6971 15.19 10.717C15.0997 10.7379 15.012 10.7689 14.928 10.809C14.8463 10.8486 14.7695 10.8978 14.699 10.955C14.628 11.0131 14.5639 11.0796 14.508 11.153C14.4517 11.2283 14.4048 11.3101 14.368 11.397C14.3307 11.484 14.3041 11.5753 14.289 11.67C14.2731 11.7646 14.2686 11.8609 14.276 11.956C14.2828 12.0507 14.3014 12.1441 14.331 12.234C14.3613 12.3218 14.4023 12.4056 14.453 12.483C14.5052 12.5585 14.5661 12.6276 14.635 12.689C14.7057 12.7487 14.7836 12.7998 14.867 12.841C14.9513 12.8829 15.04 12.9149 15.131 12.936C15.2222 12.9581 15.3156 12.9691 15.409 12.969C15.5016 12.9692 15.5939 12.9595 15.685 12.94C15.7743 12.9207 15.8611 12.8917 15.944 12.854C16.0226 12.8169 16.097 12.7714 16.166 12.719C16.2327 12.6667 16.2938 12.6078 16.348 12.543C16.4016 12.4781 16.4476 12.4069 16.485 12.331C16.5268 12.2523 16.5574 12.1683 16.575 12.082L9 18V9L16 11.37Z"
                  stroke="#EF4444"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </Box>
            Business Goals & Metrics
          </Typography>

          <Box
            sx={{
              display: "grid",
              gap: 2.5,
            }}
          >
            <Box>
              <Typography
                variant="subtitle2"
                sx={{
                  color: "#6B7280",
                  fontWeight: 600,
                  fontSize: "0.85rem",
                  mb: 0.7,
                }}
              >
                Revenue Target
              </Typography>
              <Typography
                sx={{
                  color: "#374151",
                  fontSize: "0.95rem",
                  fontWeight: 500,
                }}
              >
                {project.businessGoals.shortTermRevenueTargets}
              </Typography>
            </Box>
            <Box>
              <Typography
                variant="subtitle2"
                sx={{
                  color: "#6B7280",
                  fontWeight: 600,
                  fontSize: "0.85rem",
                  mb: 0.7,
                }}
              >
                Growth Objectives
              </Typography>
              <Typography
                sx={{
                  color: "#374151",
                  fontSize: "0.95rem",
                }}
              >
                {project.businessGoals.longTermGrowthObjectives}
              </Typography>
            </Box>
            <Box>
              <Typography
                variant="subtitle2"
                sx={{
                  color: "#6B7280",
                  fontWeight: 600,
                  fontSize: "0.85rem",
                  mb: 0.7,
                }}
              >
                Target CAC
              </Typography>
              <Typography
                sx={{
                  color: "#374151",
                  fontSize: "0.95rem",
                  fontWeight: 500,
                }}
              >
                {
                  project.budgetResourceAllocation
                    .customerAcquisitionCostTargets
                }
              </Typography>
            </Box>
            <Box>
              <Typography
                variant="subtitle2"
                sx={{
                  color: "#6B7280",
                  fontWeight: 600,
                  fontSize: "0.85rem",
                  mb: 0.7,
                }}
              >
                Marketing Budget
              </Typography>
              <Typography
                sx={{
                  color: "#374151",
                  fontSize: "0.95rem",
                  fontWeight: 500,
                }}
              >
                {project.budgetResourceAllocation.marketingBudget}
              </Typography>
            </Box>
          </Box>
        </Paper>
      </Grid>
    </Grid>
  );

  const LoadingHeader = () => (
    <Box
      sx={{
        mb: 4,
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
      }}
    >
      <Box>
        <Skeleton variant="text" width={300} height={40} sx={{ mb: 1 }} />
        <Skeleton variant="text" width={200} height={24} />
      </Box>
    </Box>
  );

  const LoadingAgentCard = () => (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        mb: 3,
        border: "1px solid rgba(0, 0, 0, 0.08)",
        borderRadius: "12px",
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Skeleton variant="circular" width={44} height={44} />
          <Skeleton variant="text" width={160} />
        </Box>
      </Box>
      <Skeleton variant="text" width="90%" />
      <Skeleton variant="text" width="80%" />
      <Skeleton variant="text" width="60%" sx={{ mt: 2 }} />
    </Paper>
  );

  const LoadingDetailsGrid = () => (
    <Grid container spacing={3} sx={{ mt: 4 }}>
      {[1, 2, 3, 4].map((i) => (
        <Grid item xs={12} md={6} key={i}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              border: "1px solid rgba(0, 0, 0, 0.08)",
              borderRadius: "12px",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
              <Skeleton
                variant="rectangular"
                width={24}
                height={24}
                sx={{ borderRadius: "6px" }}
              />
              <Skeleton variant="text" width={180} height={32} />
            </Box>
            <Box
              sx={{ display: "grid", gridTemplateColumns: "1fr 2fr", gap: 2 }}
            >
              {[1, 2, 3, 4].map((j) => (
                <React.Fragment key={j}>
                  <Skeleton variant="text" width="80%" />
                  <Skeleton variant="text" width="90%" />
                </React.Fragment>
              ))}
            </Box>
          </Paper>
        </Grid>
      ))}
    </Grid>
  );

  return (
    <CommonLayout>
      <Box
        sx={{
          p: { xs: 2, sm: 3 },
          backgroundColor: "#FAFAFA",
        }}
      >
        {isLoading ? (
          <>
            <LoadingHeader />
            <LoadingAgentCard />
            <LoadingAgentCard />
            <LoadingAgentCard />
            <LoadingAgentCard />
            <LoadingDetailsGrid />
          </>
        ) : (
          <>
            <Box
              sx={{
                mb: 4,
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                justifyContent: "space-between",
                alignItems: { xs: "flex-start", sm: "center" },
                gap: { xs: 1, sm: 0 },
              }}
            >
              <Box>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 700,
                    fontSize: { xs: "1.5rem", sm: "1.75rem" },
                    color: "#111827",
                    mb: 0.5,
                  }}
                >
                  {project.basicCompanyInfo.companyName}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: "rgba(107, 114, 128, 0.8)",
                    fontSize: "0.875rem",
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                  }}
                >
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M21 8L12 17L3 8"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  Project ID: {projectId}
                </Typography>
              </Box>
            </Box>

            {selectedAgent ? (
              renderAgentCards()
            ) : (
              <>
                <Typography
                  variant="h6"
                  sx={{
                    mb: 3,
                    fontWeight: 600,
                    fontSize: "1.1rem",
                    color: "#111827",
                  }}
                >
                  Select an Agent
                </Typography>
                {renderAgentCards()}
                {renderProjectDetails()}
              </>
            )}
          </>
        )}
      </Box>
    </CommonLayout>
  );
};

export default SelectedProject;
